import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Music2, Heart, Mail, Youtube, Zap } from "lucide-react";
import { getChannel } from "@/lib/api";
import SearchBar from "@/components/SearchBar";

const Layout = ({ children }) => {
  const [channel, setChannel] = useState(null);
  const location = useLocation();

  useEffect(() => {
    getChannel().then(setChannel).catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="relative z-10 min-h-screen flex flex-col" data-testid="app-layout">
      <Header channel={channel} />
      <main className="flex-1">{children}</main>
      <Footer channel={channel} />
    </div>
  );
};

const Header = ({ channel }) => {
  const location = useLocation();
  const onWatch = location.pathname.startsWith("/watch");

  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-xl bg-[#0a0a0b]/80 border-b border-[var(--border)]"
      data-testid="site-header"
    >
      <div className={`mx-auto flex items-center justify-between px-4 sm:px-8 py-3 ${onWatch ? "max-w-[1600px]" : "max-w-[1400px]"}`}>
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <div className="relative">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[var(--accent)] shadow-[0_0_20px_rgba(200,16,46,0.35)]">
              {channel?.avatar ? (
                <img src={channel.avatar} alt="Abel Andrade" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[var(--accent)] flex items-center justify-center">
                  <Music2 className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[var(--accent)] pulse-dot border-2 border-[#0a0a0b]"></span>
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-xl font-semibold tracking-tight">
              Abel <span className="text-[var(--accent)]">Andrade</span>
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--text-muted)]">
              Criações com alma · 🇵🇹
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3" data-testid="header-nav">
          <div className="hidden md:block">
            <SearchBar />
          </div>
          <NavLink to="/" label="Início" testid="nav-home" />
          <NavLink to="/shorts" label="Shorts" testid="nav-shorts" icon={<Zap className="w-3.5 h-3.5" />} />
          <NavLink to="/sobre" label="Sobre" testid="nav-about" />
          <a
            href={channel?.youtube_url || "https://www.youtube.com/@abelandrade-youtube"}
            target="_blank"
            rel="noreferrer"
            className="pill pill-primary"
            data-testid="header-subscribe-button"
          >
            <Youtube className="w-4 h-4" />
            <span className="hidden sm:inline">Subscrever</span>
          </a>
        </nav>
      </div>
      <div className="md:hidden px-4 pb-3">
        <SearchBar />
      </div>
    </header>
  );
};

const NavLink = ({ to, label, testid, icon }) => {
  const { pathname } = useLocation();
  const active = pathname === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-medium px-3 py-2 rounded-full transition-colors inline-flex items-center gap-1 ${
        active ? "text-white bg-[var(--bg-hover)]" : "text-[var(--text-dim)] hover:text-white"
      }`}
    >
      {icon}
      {label}
    </Link>
  );
};

const Footer = ({ channel }) => {
  return (
    <footer className="border-t border-[var(--border)] mt-20" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12 grid sm:grid-cols-3 gap-8">
        <div>
          <div className="font-display text-2xl font-semibold">
            Abel <span className="text-[var(--accent)]">Andrade</span>
          </div>
          <p className="text-[var(--text-dim)] text-sm mt-2 max-w-sm">
            Música que toca, inspira e acompanha. Hinos, fado, oração e tributos — feitos com alma e coração.
          </p>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Navegação</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-[var(--accent)] transition-colors">Vídeos</Link></li>
            <li><Link to="/sobre" className="hover:text-[var(--accent)] transition-colors">Sobre o canal</Link></li>
            <li>
              <a href={channel?.youtube_url || "#"} target="_blank" rel="noreferrer" className="hover:text-[var(--accent)] transition-colors">
                YouTube oficial
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Contacto</h4>
          <a
            href={`mailto:${channel?.contact || "andradeabel@hotmail.com"}`}
            className="inline-flex items-center gap-2 text-sm hover:text-[var(--accent)] transition-colors"
            data-testid="footer-email"
          >
            <Mail className="w-4 h-4" />
            {channel?.contact || "andradeabel@hotmail.com"}
          </a>
          <p className="text-xs text-[var(--text-muted)] mt-6 flex items-center gap-1">
            Feito com <Heart className="w-3 h-3 text-[var(--accent)] fill-[var(--accent)]" /> em Portugal
          </p>
        </div>
      </div>
      <div className="border-t border-[var(--border)] py-4 text-center text-xs text-[var(--text-muted)]">
        © {new Date().getFullYear()} Abel Andrade · Todos os direitos reservados
      </div>
    </footer>
  );
};

export default Layout;
